import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import {
  useConfigurationQuery,
  GET_COMPANY_MANIFEST_QUERY_KEY,
} from 'theSource/queries';
import { selectNativeAuth } from 'theSource/services';
import { hasDeniedPermissions, useApiMutation } from 'theSource/utils';
import {
  AuthenticationDatatDto,
  AuthenticationErrorDto,
  AuthenticationOptionsDataDto,
  AuthenticationOptionsResponseDto,
  AuthenticationResponseDto,
} from '../types';
import { LOGON_NEW_QUERY_KEY, LOGON_QUERY_KEY } from './constants';
import { addSnackbar } from 'theSource/components';
import {
  mapToAuthenticationData,
  mapToCredentialRequestOptions,
} from './mappingUtils';
import { CompanyManifestResponseDto } from 'theSource/queries/useCompanyManifestQuery/types';

export interface UseNativeAuthResult {
  isNativeAuthFlowEnabled: boolean;
  isNativeAuthInProgress: boolean;
  isNativeAuthCompleted: boolean;
  authenticate: () => Promise<void>;
}

export const useNativeAuth = (): UseNativeAuthResult => {
  const {
    username,
    isAutomaticAuthDisabled,
    isSupported,
    isEnabled,
    isRegistered,
  } = useSelector(selectNativeAuth);
  const { configuration } = useConfigurationQuery();
  const [isNativeAuthInProgress, setIsNativeAuthInProgress] = useState(false);
  const [isNativeAuthFlowEnabled, setIsNativeAuthFlowEnabled] = useState(false);
  const [isNativeAuthCompleted, setIsNativeAuthCompleted] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const baseUrl = configuration?.apiHost;

  const queryClient = useQueryClient();
  const manifestData: CompanyManifestResponseDto = queryClient.getQueryData(
    GET_COMPANY_MANIFEST_QUERY_KEY
  )!;

  const { mutateAsync: logonNew } = useApiMutation<
    AuthenticationOptionsResponseDto,
    AuthenticationErrorDto,
    AuthenticationOptionsDataDto
  >({
    queryKey: LOGON_NEW_QUERY_KEY,
    endpoint: `${baseUrl}/web_authn/logon/new`,
    method: 'post',
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
  });

  const { mutateAsync: logon } = useApiMutation<
    AuthenticationResponseDto,
    AuthenticationErrorDto,
    AuthenticationDatatDto
  >({
    queryKey: LOGON_QUERY_KEY,
    endpoint: `${baseUrl}/web_authn/logon`,
    method: 'post',
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
  });

  const handleAuthenticationFlow = useCallback(async (): Promise<void> => {
    if (!isNativeAuthFlowEnabled) {
      throw new Error('Native authentication flow is disabled');
    }

    const authenticationOptions = await logonNew({
      user_name: username!,
      organization_id: manifestData!.organization_id,
    });

    const credentialRequestOptions = mapToCredentialRequestOptions(
      authenticationOptions
    );

    const credential = await navigator.credentials.get(
      credentialRequestOptions
    );

    if (!credential) {
      throw new Error('Authentication was not completed');
    }

    const authenticationData = mapToAuthenticationData(
      credential as PublicKeyCredential
    );

    await logon(authenticationData);
  }, [logon, logonNew, username, isNativeAuthFlowEnabled, manifestData]);

  const authenticate = useCallback(async () => {
    try {
      setIsNativeAuthInProgress(true);
      await handleAuthenticationFlow();
      setIsNativeAuthInProgress(false);
      setIsNativeAuthCompleted(true);
    } catch (error) {
      setIsNativeAuthInProgress(false);
      setIsNativeAuthCompleted(false);
      if (hasDeniedPermissions(error)) {
        return;
      }
      dispatch(
        addSnackbar({
          message: t('nativeAuthError'),
          severity: 'error',
          position: 'bottom',
          disableAutoHide: true,
        })
      );
    }
  }, [handleAuthenticationFlow, dispatch, t]);

  useEffect(() => {
    if (isEnabled && isSupported && isRegistered && Boolean(username)) {
      setIsNativeAuthFlowEnabled(true);

      if (!isAutomaticAuthDisabled) {
        setIsNativeAuthInProgress(true);
      }
    }
  }, [isEnabled, isSupported, isRegistered, username, isAutomaticAuthDisabled]);

  return {
    isNativeAuthFlowEnabled,
    isNativeAuthInProgress,
    isNativeAuthCompleted,
    authenticate,
  };
};
